import React, { useRef, useState } from "react";
import { motion, useInView } from "framer-motion";
import { Play } from "lucide-react";

// Film grain overlay
export const Grain = () => <div className="grain-overlay" aria-hidden="true" />;

// Masked line-by-line text reveal (signature on-load / scroll moment)
export const RevealLines = ({ lines, className = "", delay = 0, testid }) => (
  <span className={className} data-testid={testid}>
    {lines.map((line, i) => (
      <span key={i} className="block overflow-hidden">
        <motion.span
          className="block"
          initial={{ y: "110%" }}
          animate={{ y: 0 }}
          transition={{
            duration: 0.9,
            delay: delay + i * 0.12,
            ease: [0.22, 1, 0.36, 1],
          }}
        >
          {line}
        </motion.span>
      </span>
    ))}
  </span>
);

// Scroll reveal wrapper (fade + rise)
export const Reveal = ({ children, className = "", y = 40, delay = 0, once = true }) => {
  const ref = useRef(null);
  const inView = useInView(ref, { once, margin: "-10% 0px -10% 0px" });
  return (
    <motion.div
      ref={ref}
      className={className}
      initial={{ opacity: 0, y }}
      animate={inView ? { opacity: 1, y: 0 } : {}}
      transition={{ duration: 0.9, delay, ease: [0.22, 1, 0.36, 1] }}
    >
      {children}
    </motion.div>
  );
};

// Masked line that reveals on scroll into view
export const RevealHeading = ({ text, className = "", testid }) => {
  const ref = useRef(null);
  const inView = useInView(ref, { once: true, margin: "-15% 0px" });
  return (
    <span ref={ref} className={`block overflow-hidden ${className}`} data-testid={testid}>
      <motion.span
        className="block"
        initial={{ y: "110%" }}
        animate={inView ? { y: 0 } : {}}
        transition={{ duration: 1, ease: [0.22, 1, 0.36, 1] }}
      >
        {text}
      </motion.span>
    </span>
  );
};

export const Kicker = ({ children, index, className = "" }) => (
  <div className={`flex items-center gap-3 ${className}`}>
    {index && <span className="font-mono-c text-xs text-[hsl(var(--accent))]">{index}</span>}
    <span className="kicker">{children}</span>
  </div>
);

// Click-to-play video: grayscale thumbnail (highlights on hover) -> YouTube player (Drive fallback via fileId)
export const PlayableVideo = ({ fileId, youtubeId, thumbnail, alt, testid, aspect = "aspect-[16/7]", caption }) => {
  const [playing, setPlaying] = useState(false);

  if (playing) {
    const src = youtubeId
      ? `https://www.youtube.com/embed/${youtubeId}?autoplay=1&rel=0&modestbranding=1&playsinline=1`
      : `https://drive.google.com/file/d/${fileId}/preview`;
    return (
      <div className={`relative w-full ${aspect} bg-[#0a0a0a] border border-border overflow-hidden`}>
        <iframe
          title={alt}
          src={src}
          allow="autoplay; encrypted-media; fullscreen"
          allowFullScreen
          className="absolute inset-0 h-full w-full"
          data-testid={testid}
        />
      </div>
    );
  }

  return (
    <button
      onClick={() => setPlaying(true)}
      className={`group relative block w-full ${aspect} bg-[#0a0a0a] border border-border overflow-hidden cursor-pointer`}
      data-testid={`${testid}-thumb`}
      aria-label={`Play ${alt || "video"}`}
    >
      <img
        src={thumbnail}
        alt={alt}
        className="absolute inset-0 h-full w-full object-cover grayscale-[0.7] group-hover:grayscale-0 group-hover:scale-[1.03] transition-all duration-300"
      />
      <div className="absolute inset-0 bg-background/20 group-hover:bg-background/5 transition-colors duration-300" />
      <span className="absolute inset-0 flex items-center justify-center">
        <span className="flex h-14 w-14 md:h-16 md:w-16 items-center justify-center rounded-full border border-white/70 bg-white/10 backdrop-blur-sm group-hover:bg-white/20 group-hover:scale-110 transition-all duration-300">
          <Play size={22} className="text-white fill-white ml-0.5" />
        </span>
      </span>
      {caption && (
        <span className="absolute bottom-5 left-5 md:bottom-6 md:left-6 kicker">{caption}</span>
      )}
    </button>
  );
};
